import * as React from "react"
import { type ApolloError } from "@apollo/client"
import {
  type Contact,
  type ResetPasswordMutation,
  useResetPasswordMutation,
} from "@digits-graphql/frontend/graphql-public"
import Form from "@digits-shared/components/UI/Elements/Form/Form"
import { Password, RequirementStatus } from "@digits-shared/components/UI/Elements/Form/Password"
import { DigitsButton } from "@digits-shared/DesignSystem/Button"
import errorHelper from "@digits-shared/helpers/errorHelper"
import useRouter from "@digits-shared/hooks/useRouter"
import useSession from "@digits-shared/hooks/useSession"
import styled from "styled-components"
import { ButtonWrapper } from "src/frontend/components/Public/Shared/RegistrationElements"
import { sessionError } from "src/frontend/components/Session/Glow/error"
import { UserInfo } from "src/frontend/components/Session/Glow/PasswordReset/UserInfo"
import { Title } from "src/frontend/components/Shared/Glow/Header"
import { useChromeAlerts } from "src/frontend/hooks/useChromeAlerts"
import routes from "src/frontend/routes"
import type FrontendSession from "src/frontend/session"

/*
  STYLES
*/

const ResetForm = styled(Form)`
  gap: 16px;
`

const PasswordContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 16px;
  justify-content: space-between;
`

/*
  COMPONENTS
*/

export const ResetPasswordForm: React.FC<{
  token: string
  contact?: Contact
}> = ({ token, contact }) => {
  const passwordOne = useValidatePassword()
  const passwordTwo = useValidatePassword()
  const { loading, onSubmit } = useResetPassword(token, passwordOne.password, passwordTwo.password)

  const disableButton = !passwordOne.valid || !passwordTwo.valid || loading

  return (
    <>
      <Title>Set Digits Password</Title>
      {contact && <UserInfo contact={contact} />}
      <ResetForm onSubmit={onSubmit}>
        <PasswordContainer>
          <Password
            useNewInput
            marginLeft={12}
            name="passwordOne"
            placeholder="Enter Password"
            label="Enter Password"
            autocomplete="new-password"
            hideLabel
            validate={passwordOne.validate}
            disabled={loading}
          />
          <Password
            useNewInput
            marginLeft={12}
            name="passwordTwo"
            placeholder="Re-Enter Password"
            label="Re-Enter Password"
            autocomplete="new-password"
            hideLabel
            validate={passwordTwo.validate}
            validateMatch={passwordOne.validateMatch}
            disabled={loading}
          />
        </PasswordContainer>
        <ButtonWrapper disabled={disableButton}>
          <DigitsButton
            $fullWidth
            id="save-password"
            disabled={disableButton}
            type="submit"
            loading={loading}
            label="Save Password"
          />
        </ButtonWrapper>
      </ResetForm>
    </>
  )
}

function useResetPassword(token: string, passwordOne: string, passwordTwo: string) {
  const session = useSession<FrontendSession>()
  const { history } = useRouter()
  const { alertError } = useChromeAlerts()

  const onError = React.useCallback(
    (error: ApolloError) => {
      let message = errorHelper.errorMessage()
      const firstError = error.graphQLErrors[0]
      if (firstError) {
        message = firstError.message
      }
      TrackJS?.track(`Password reset error: ${message}`)
      alertError(sessionError)
    },
    [alertError]
  )

  const onCompleted = React.useCallback(
    ({ resetPassword }: ResetPasswordMutation) => {
      if (!resetPassword || !resetPassword.bearer) {
        TrackJS?.track("Password reset error")
        alertError(sessionError)
        return
      }

      return session.onBearerChange(resetPassword).then(() => {
        history.replace(routes.root.generate())
      })
    },
    [alertError, history, session]
  )
  const [resetPassword, { loading }] = useResetPasswordMutation({
    context: { publicAPI: true },
    onError,
    onCompleted,
  })

  const onSubmit = React.useCallback(
    (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault()

      if (passwordOne !== passwordTwo) {
        alertError("Passwords do not match!")
        return
      }

      return resetPassword({
        variables: {
          resetToken: token,
          newPassword: passwordOne,
        },
      })
    },
    [passwordOne, passwordTwo, token, resetPassword, alertError]
  )

  return React.useMemo(
    () => ({
      onSubmit,
      loading,
    }),
    [onSubmit, loading]
  )
}

function useValidatePassword() {
  const [password, setPassword] = React.useState("")
  const [valid, setValid] = React.useState(false)
  const validate = React.useCallback((pw: string, status: RequirementStatus) => {
    setPassword(pw)
    setValid(status === RequirementStatus.Pass)
  }, [])
  const validateMatch = React.useCallback((pw: string) => pw === password, [password])

  return React.useMemo(
    () => ({
      password,
      valid,
      validate,
      validateMatch,
    }),
    [password, valid, validate, validateMatch]
  )
}
